import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Subscription } from 'rxjs';
import { MoneyService } from '../money.service';
import { Txn, Category, Group } from '../money.model';
import { formatCents } from '../format';
import { todayISO } from '../input';
import { detectRecurring, RecurringSeries } from '../rules/recurring.detector';
import { stateForLedger, allRecurringSeries, visibleSeries } from '../recurring/recurring.calc';
import { LinePoint } from '../charts/line-chart.component';
import {
  MonthHeadline, GroupLine, MerchantSplit, YearHeadline, YearCategoryLine,
  reportMonths, monthHeadline, groupTable, merchantSplit, largestTxns,
  reportYears, yearHeadline, netByMonth, yearCategoryTable, yoyCoverageNote,
} from './reports.calc';

@Component({
  selector: 'app-money-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  standalone: false,
})
export class ReportsComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';

  view: 'month' | 'year' = 'month';
  month = '';
  months: string[] = [];
  year = '';
  years: string[] = [];

  headline: MonthHeadline | null = null;
  groupLines: GroupLine[] = [];
  split: MerchantSplit = { bills: [], everyday: [] };
  largest: Txn[] = [];

  yHeadline: YearHeadline | null = null;
  yCategories: YearCategoryLine[] = [];
  ySplit: MerchantSplit = { bills: [], everyday: [] };
  yNetPoints: LinePoint[] = [];
  yNetDisplay: string[] = [];
  yCoverageNote: string | null = null;

  protected all: Txn[] = [];
  protected categories: Category[] = [];
  protected groups: Group[] = [];
  protected series: RecurringSeries[] = [];

  private subs: Subscription[] = [];
  private innerSubs: Subscription[] = [];

  readonly fmt = formatCents;

  constructor(private afAuth: AngularFireAuth, private money: MoneyService) {}

  ngOnInit(): void {
    this.subs.push(this.afAuth.authState.subscribe(user => {
      if (!user) return;
      // Fresh streams per login; the previous login's streams are released
      // rather than accumulated — same shape as savings.component.ts.
      this.innerSubs.forEach(s => s.unsubscribe());
      this.innerSubs = [];
      this.innerSubs.push(this.money.subscribeAll(user.uid).subscribe({
        next: data => {
          this.all = data.txns;
          this.categories = data.categories;
          this.groups = data.groups;
          // Same pipeline recurring.component.ts uses (read it and follow it
          // exactly) — raw detectRecurring output ignores the user's own
          // pin/dismiss decisions, which billStems' own docstring promises
          // ("detected or pinned") but raw output cannot deliver (Finding 3).
          const asOf = todayISO();
          const detected = detectRecurring(data.txns, asOf);
          const state = stateForLedger(data.txns, detected, data.recurringState);
          const all = allRecurringSeries(data.txns, detected, state, asOf);
          this.series = visibleSeries(all, state);
          this.months = reportMonths(data.txns);
          if (!this.month || !this.months.includes(this.month)) {
            this.month = this.months[0] ?? '';
          }
          this.years = reportYears(data.txns);
          if (!this.year || !this.years.includes(this.year)) {
            this.year = this.years[0] ?? '';
          }
          this.recompute();
          this.loading = false;
        },
        error: (e: unknown) => {
          this.error = `Could not load your data: ${(e as Error).message}. Try reloading the page.`;
          this.loading = false;
        },
      }));
    }));
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.innerSubs.forEach(s => s.unsubscribe());
  }

  setMonth(month: string): void { this.month = month; this.recompute(); }
  setView(view: 'month' | 'year'): void { this.view = view; this.recompute(); }
  setYear(year: string): void { this.year = year; this.recompute(); }

  protected recompute(): void {
    if (this.view === 'month' && this.month) {
      this.headline = monthHeadline(this.all, this.month);
      this.groupLines = groupTable(this.all, this.categories, this.groups, this.month);
      this.split = merchantSplit(this.all, this.series, this.month);
      this.largest = largestTxns(this.all, this.month);
    }
    if (this.view === 'year' && this.year) {
      this.yHeadline = yearHeadline(this.all, this.year);
      this.yCategories = yearCategoryTable(this.all, this.categories, this.groups, this.year);
      this.ySplit = merchantSplit(this.all, this.series, this.year);
      const pts = netByMonth(this.all, this.year);
      this.yNetPoints = pts.map(p => ({ label: p.label, value: p.cents }));
      this.yNetDisplay = pts.map(p => formatCents(p.cents));
      this.yCoverageNote = yoyCoverageNote(this.all, this.year);
    }
  }

  trackGroup(_: number, g: GroupLine): string { return g.id; }
  trackStem(_: number, m: { stem: string }): string { return m.stem; }
  trackTxn(_: number, t: Txn): string { return t.id; }
  trackYearCat(_: number, r: YearCategoryLine): string { return r.id; }
}
